/**
 * CalendarRoomCell - Cel·la per mostrar info de l'habitació (fila esquerra)
 *
 * Color pla de `CALENDAR_PALETTE` (`plans/calendar-visual-refresh.md` §3); amb la fila en hover
 * (`group/row` de la graella) passa a `rowHover`, com les cel·les lliures de la fila.
 *
 * F7: 72 px fixos (`ROOM_COLUMN_WIDTH`) i una sola línia, «H101 PB» (nom en negreta i la planta
 * abreujada en gris); el `title` ho diu sencer («H101 · Planta baixa»).
 *
 * Mobile-first, modular
 */

import { cn } from '@/lib/utils';
import { CALENDAR_PALETTE } from './calendar-theme';
import { floorAbbreviation, ROOM_COLUMN_WIDTH, roomCellTitle } from './room-column';

export interface RoomInfo {
  id: string;
  displayName: string;
  name?: string; // Nom original Beds24 (per ordenació alternativa)
  physicalLocation?: string;
  beds24RoomId?: number;
  beds24UnitId?: number;
  sortOrder?: number; // Per ordenació física
}

export interface CalendarRoomCellProps {
  room: RoomInfo;
  onClick?: (room: RoomInfo) => void;
  className?: string;
}

export function CalendarRoomCell({ room, onClick, className }: CalendarRoomCellProps) {
  const handleClick = () => {
    onClick?.(room);
  };
  const floor = floorAbbreviation(room.physicalLocation);

  return (
    <div
      onClick={handleClick}
      title={roomCellTitle(room)}
      data-testid="room-cell"
      style={
        {
          '--cal-room-bg': CALENDAR_PALETTE.header,
          '--cal-room-hover-bg': CALENDAR_PALETTE.rowHover,
          borderRightColor: CALENDAR_PALETTE.separator,
          width: ROOM_COLUMN_WIDTH,
          minWidth: ROOM_COLUMN_WIDTH,
        } as React.CSSProperties
      }
      className={cn(
        // Base: amplada fixa (casa amb la cantonada de la capçalera), una sola línia centrada
        'sticky left-0 z-[22] flex shrink-0 items-center',
        'px-1.5 border-r-2',
        'bg-[var(--cal-room-bg)] group-hover/row:bg-[var(--cal-room-hover-bg)]',
        'transition-colors duration-150',
        'touch-manipulation',

        // Cursor
        onClick && 'cursor-pointer',

        className,
      )}
    >
      {/* «H101 PB»: el nom es talla amb «…» si no hi cap; la planta sempre es veu. */}
      <div className="flex min-w-0 items-baseline gap-1 leading-tight">
        <span className="truncate text-sm font-bold text-gray-900">{room.displayName}</span>
        {floor && (
          <span className="shrink-0 text-[11px] font-normal text-gray-500" data-room-floor>
            {floor}
          </span>
        )}
      </div>
    </div>
  );
}

export default CalendarRoomCell;
